import { useQuery } from '@tanstack/react-query';
import { useState } from 'react';

import { api, getProviderNameFull, getQueryProps, isDefined, isTruthy } from 'common';

import { BlockAutocomplete } from './Autocomplete';
import { useSelectedNodeField } from '../useSelectedNodeField';

interface SearchableProviderPickerProps {
  fieldName: string;
  label: string;
  organizationId: string;
  helperText?: string;
}

export function SearchableProviderPicker(props: SearchableProviderPickerProps) {
  const { fieldName, label, organizationId, helperText } = props;
  const [searchInput, setSearchInput] = useState<string>('');
  const { field } = useSelectedNodeField<string[] | undefined>(fieldName);
  const { value: ids } = field;

  const { data: providerOptions } = useQuery({
    ...getQueryProps(api.client.User, 'findAll', {
      practiceOrganizationId: organizationId,
      role: api.User.Role.Provider,
      limit: 20,
      q: searchInput,
    }),
    enabled: isTruthy(searchInput.length),
    select: (data) => data.users,
  });

  const { data: selectedProviders } = useQuery({
    ...getQueryProps(api.client.User, 'findAll', {
      id: ids,
    }),
    enabled: isTruthy(ids?.length),
    select: (data) => data.users,
  });

  const providerOptionsMap = Object.fromEntries(
    providerOptions?.map((provider) => [provider.id, provider]) ?? []
  );
  const selectedProvidersMap = Object.fromEntries(
    selectedProviders?.map((provider) => [provider.id, provider]) ?? []
  );
  const entitiesMap = { ...selectedProvidersMap, ...providerOptionsMap };

  return (
    <BlockAutocomplete
      fieldName={fieldName}
      label={label}
      options={Object.keys(providerOptionsMap)}
      filterOptions={(options) => options}
      freeSolo={false}
      getOptionLabel={(id) =>
        isDefined(entitiesMap[id]) ? getProviderNameFull({ provider: entitiesMap[id] }) : id
      }
      getOptionKey={(id) => id}
      onDebouncedInputEffect={setSearchInput}
      multiple
      helperText={helperText}
    />
  );
}
